import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useGallery } from '@vibesites/runtime/react';
import {
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useComfyCtaStyles } from '../hooks/useComfyCtaStyles';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/* ---------------------------------------------------------------------------
 * Smart photo selection
 * The three cards default to photo_gallery[0..2], but a property's gallery is
 * often ordered so that logos, floor-plan schematics, bathrooms, laundry rooms,
 * and other low-appeal shots land in those slots. To keep the section
 * attractive before AI generation runs, we score the gallery at render time:
 * hard-exclude anything that shouldn't create a first impression (logos,
 * signage, floor plans, bathrooms, laundry, utility, parking) plus honor the
 * category conventions the Gallery page uses (title = "logo" | "floorplan" |
 * "other" is never a viable pick). Then we pick per CTA:
 *   - Card 1 "Spacious Layouts"     → interior apartment shots
 *   - Card 2 "Picture Yourself Here" → lifestyle (interior or community)
 *   - Card 3 "Convenience All Around" → community amenities / exterior
 * A manual editor override always wins over the smart pick.
 * ------------------------------------------------------------------------- */

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  alt_text?: string | null;
  description?: string | null;
  original_url?: string | null;
  source_page_urls?: string | null;
}

/**
 * Titles the Gallery page treats as non-photo categories. If the source data
 * classifies a photo as one of these, we never surface it here either.
 */
const EXCLUDED_TITLES = new Set(['logo', 'floorplan', 'floor plan', 'other']);

/**
 * Substrings that disqualify a photo from being a first-impression image.
 * Covers the low-quality / low-appeal cases the user called out: logos,
 * signage, floor-plan schematics, bathrooms, laundry, utility, parking.
 */
const EXCLUDE_KEYWORDS = [
  // Branding / non-photos
  'logo', 'signage', 'wordmark', 'brandmark', 'watermark',
  // Floor plans and site maps
  'floorplan', 'floor plan', 'siteplan', 'site plan', 'schematic', 'diagram',
  // Bathrooms / laundry / closets / utility
  'bath', 'restroom', 'toilet', 'shower', 'tub', 'vanity',
  'laundry', 'washer', 'dryer', 'utility', 'mechanical', 'closet', 'hvac',
  // Parking / trash / back-of-house
  'parking', 'garage', 'trash', 'dumpster', 'storage', 'maintenance',
];

const INTERIOR_KEYWORDS = [
  'apartment', 'unit', 'interior', 'model', 'home', 'suite', 'residence',
  'living', 'living room', 'family room', 'great room',
  'kitchen', 'kitchen island', 'countertop', 'cabinetry', 'finishes',
  'bedroom', 'primary bedroom', 'master bedroom',
  'dining', 'dining room', 'open concept', 'floor-to-ceiling',
];

const INDOOR_AMENITY_KEYWORDS = [
  'clubhouse', 'club house', 'lounge', 'fitness', 'gym', 'business center',
  'coworking', 'co-work', 'game', 'media', 'cyber', 'coffee', 'resident',
  'common', 'theater', 'yoga', 'wellness', 'spa',
];

const COMMUNITY_KEYWORDS = [
  'pool', 'poolside', 'courtyard', 'exterior', 'building', 'facade',
  'grounds', 'landscap', 'patio', 'grill', 'rooftop', 'deck', 'outdoor',
  'entrance', 'community', 'park', 'playground', 'dog', 'cabana',
  'fire pit', 'firepit', 'lawn', 'terrace', 'balcony',
];

function normalizedTitle(p: GalleryPhoto): string {
  return (p.title || '').toLowerCase().trim();
}

function photoText(p: GalleryPhoto): string {
  return [p.title, p.description, p.alt_text, p.image, p.original_url, p.source_page_urls]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function isExcludedPhoto(p: GalleryPhoto): boolean {
  if (EXCLUDED_TITLES.has(normalizedTitle(p))) return true;
  const text = photoText(p);
  return EXCLUDE_KEYWORDS.some((kw) => text.includes(kw));
}

/**
 * Photo category from the source `title` field. Matches the Gallery page's
 * `classifyPhoto` helper so both surfaces stay consistent.
 */
function photoCategory(p: GalleryPhoto): 'apartment' | 'community' | 'other' {
  const title = normalizedTitle(p);
  if (title.includes('apartment')) return 'apartment';
  if (title.includes('community')) return 'community';
  return 'other';
}

function keywordScore(p: GalleryPhoto, keywords: string[]): number {
  const text = photoText(p);
  return keywords.reduce((sum, kw) => sum + (text.includes(kw) ? 1 : 0), 0);
}

/**
 * Composite score for a card. A matching source category is worth a large
 * bump (weight 3) so a photo the property model already labels "apartment"
 * beats a random shot with one keyword hit. Keyword hits refine the ordering
 * within a category.
 */
function scoreForCard(
  p: GalleryPhoto,
  keywords: string[],
  preferredCategory: 'apartment' | 'community' | null,
): number {
  const kw = keywordScore(p, keywords);
  const cat = preferredCategory && photoCategory(p) === preferredCategory ? 3 : 0;
  return kw + cat;
}

/** Pick the highest-scoring unused, non-excluded photo for a category. */
function pickPhoto(
  gallery: GalleryPhoto[],
  keywords: string[],
  preferredCategory: 'apartment' | 'community' | null,
  used: Set<string>,
): GalleryPhoto | null {
  let best: GalleryPhoto | null = null;
  let bestScore = 0;
  for (const p of gallery) {
    if (!p.image || used.has(p.image) || isExcludedPhoto(p)) continue;
    const score = scoreForCard(p, keywords, preferredCategory);
    if (score > bestScore) {
      best = p;
      bestScore = score;
    }
  }
  // Fallback: no keyword/category match — take the first unused, non-excluded photo.
  if (!best) {
    best = gallery.find((p) => p.image && !used.has(p.image) && !isExcludedPhoto(p)) ?? null;
  }
  if (best?.image) used.add(best.image);
  return best;
}

/**
 * Resolve a card's image: honor a manual/custom editor pick, otherwise fall
 * back to the smart pick. A slot value that resolves to an excluded gallery
 * photo (the raw default ordering — a logo, floor plan, bathroom, etc.) is
 * treated as "not chosen" and replaced with the smart pick.
 */
function resolveCardImage(
  slotUrl: string | null | undefined,
  slotAlt: string | null | undefined,
  smart: GalleryPhoto | null,
  gallery: GalleryPhoto[],
): { image: string; alt: string } {
  if (slotUrl) {
    const match = gallery.find((p) => p.image === slotUrl || p.original_url === slotUrl);
    // Custom upload (not in gallery) or an acceptable gallery photo → respect it.
    if (!match || !isExcludedPhoto(match)) {
      return { image: slotUrl, alt: slotAlt || match?.alt_text || '' };
    }
  }
  return { image: smart?.image ?? slotUrl ?? '', alt: smart?.alt_text ?? '' };
}

const card1ImageDef: SlotGroupDefinition = {
  label: 'Card 1 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a strong interior photo that showcases the apartment homes — a living room, bedroom, kitchen, dining area, or a shot that highlights layouts and finishes. Only use high-quality, desirable images that create a strong first impression. Do not use logos, signage, floor-plan schematics, bathrooms, laundry rooms, closets, parking, utility/mechanical/back-of-house spaces, or any blurry, dirty, or unappealing shots.',
    context:
      'Left of three feature cards under the "Spacious Layouts" heading; the image should feel like a hero-quality interior. Each of the three cards must use a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

const card2ImageDef: SlotGroupDefinition = {
  label: 'Card 2 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select an appealing lifestyle photo — either an inviting interior (living space, model unit, resident lounge, clubhouse) or a community space (pool, courtyard, rooftop) that shows what daily life at the property feels like. Only use high-quality, desirable images that create a strong first impression. Do not use logos, signage, floor-plan schematics, bathrooms, laundry rooms, closets, parking, utility/mechanical/back-of-house spaces, or any blurry, dirty, or unappealing shots.',
    context:
      'Middle of three feature cards under the "Picture Yourself Here" heading; the image should evoke lifestyle and belonging. Each of the three cards must use a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

const card3ImageDef: SlotGroupDefinition = {
  label: 'Card 3 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a strong community photo that highlights amenities, outdoor spaces, or property features — a pool, courtyard, rooftop deck, grill area, dog park, playground, fire pit, cabana, or a hero exterior/building shot. Only use high-quality, desirable images that create a strong first impression. Do not use logos, signage, floor-plan schematics, bathrooms, laundry rooms, closets, parking, utility/mechanical/back-of-house spaces, or any blurry, dirty, or unappealing shots.',
    context:
      'Right of three feature cards under the "Convenience All Around" heading; the image should showcase community amenities and outdoor property features. Each of the three cards must use a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

const card1CtaDef: SlotGroupDefinition = {
  label: 'Card 1 CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'Choose Your Home' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const card2CtaDef: SlotGroupDefinition = {
  label: 'Card 2 CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Label', type: 'text', defaultValue: 'View the Gallery' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const card3CtaDef: SlotGroupDefinition = {
  label: 'Card 3 CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Label', type: 'text', defaultValue: 'Browse Our Amenities' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

export function PropertyOverview() {
  const card1Image = useSlotGroup('propertyoverview.card1_image', card1ImageDef);
  const card2Image = useSlotGroup('propertyoverview.card2_image', card2ImageDef);
  const card3Image = useSlotGroup('propertyoverview.card3_image', card3ImageDef);

  const card1Cta = useSlotGroup('propertyoverview.card1_cta', card1CtaDef);
  const card2Cta = useSlotGroup('propertyoverview.card2_cta', card2CtaDef);
  const card3Cta = useSlotGroup('propertyoverview.card3_cta', card3CtaDef);

  const gallery = useGallery() as GalleryPhoto[];

  const cardImages = useMemo(() => {
    const used = new Set<string>();
    // Card 1 "Spacious Layouts" — apartment interior shots.
    const smart1 = pickPhoto(gallery, INTERIOR_KEYWORDS, 'apartment', used);
    // Card 2 "Picture Yourself Here" — lifestyle: interior amenity + community
    // keywords blended so either a resident lounge or a courtyard can win.
    const smart2 = pickPhoto(
      gallery,
      [...INDOOR_AMENITY_KEYWORDS, ...COMMUNITY_KEYWORDS],
      null,
      used,
    );
    // Card 3 "Convenience All Around" — community amenities / exterior.
    const smart3 = pickPhoto(gallery, COMMUNITY_KEYWORDS, 'community', used);
    return {
      card1: resolveCardImage(card1Image.image, card1Image.alt, smart1, gallery),
      card2: resolveCardImage(card2Image.image, card2Image.alt, smart2, gallery),
      card3: resolveCardImage(card3Image.image, card3Image.alt, smart3, gallery),
    };
  }, [
    gallery,
    card1Image.image, card1Image.alt,
    card2Image.image, card2Image.alt,
    card3Image.image, card3Image.alt,
  ]);

  useRevealStyles();
  useComfyCtaStyles();
  const headerRef = useScrollReveal<HTMLDivElement>();
  const cardsRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="po-section">
      <div className="po-inner">
        <div ref={headerRef} className="po-header reveal-fade-up">
          <RichTextContent
            path="propertyoverview.title"
            as="h2"
            className="po-title font-heading"
          />
          <RichTextContent
            path="propertyoverview.blurb"
            as="div"
            className="po-blurb font-body"
          />
        </div>

        <div ref={cardsRef} className="po-cards reveal-fade-up">
          {/* Card 1 */}
          <div className="po-card">
            <EditableSlotGroup groupPath="propertyoverview.card1_image" definition={card1ImageDef}>
              <div className="po-card-media">
                <VibesitesImage
                  url={cardImages.card1.image}
                  alt={cardImages.card1.alt}
                  className="po-card-img"
                  sizes="(min-width: 900px) 33vw, (min-width: 640px) 50vw, 100vw"
                />
              </div>
            </EditableSlotGroup>
            <div className="po-card-scrim" aria-hidden="true" />
            <div className="po-card-content">
              <RichTextContent
                path="propertyoverview.card1_heading"
                as="h3"
                className="po-card-heading font-heading"
              />
              <EditableSlotGroup
                groupPath="propertyoverview.card1_cta"
                definition={card1CtaDef}
                className="po-card-cta-wrap"
              >
                <a
                  href={card1Cta.url || '/floor-plans'}
                  target={card1Cta._target || '_self'}
                  className="comfy-cta"
                >
                  {card1Cta.label || 'Choose Your Home'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Card 2 */}
          <div className="po-card">
            <EditableSlotGroup groupPath="propertyoverview.card2_image" definition={card2ImageDef}>
              <div className="po-card-media">
                <VibesitesImage
                  url={cardImages.card2.image}
                  alt={cardImages.card2.alt}
                  className="po-card-img"
                  sizes="(min-width: 900px) 33vw, (min-width: 640px) 50vw, 100vw"
                />
              </div>
            </EditableSlotGroup>
            <div className="po-card-scrim" aria-hidden="true" />
            <div className="po-card-content">
              <RichTextContent
                path="propertyoverview.card2_heading"
                as="h3"
                className="po-card-heading font-heading"
              />
              <EditableSlotGroup
                groupPath="propertyoverview.card2_cta"
                definition={card2CtaDef}
                className="po-card-cta-wrap"
              >
                <a
                  href={card2Cta.url || '/gallery'}
                  target={card2Cta._target || '_self'}
                  className="comfy-cta"
                >
                  {card2Cta.label || 'View the Gallery'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Card 3 */}
          <div className="po-card">
            <EditableSlotGroup groupPath="propertyoverview.card3_image" definition={card3ImageDef}>
              <div className="po-card-media">
                <VibesitesImage
                  url={cardImages.card3.image}
                  alt={cardImages.card3.alt}
                  className="po-card-img"
                  sizes="(min-width: 900px) 33vw, (min-width: 640px) 50vw, 100vw"
                />
              </div>
            </EditableSlotGroup>
            <div className="po-card-scrim" aria-hidden="true" />
            <div className="po-card-content">
              <RichTextContent
                path="propertyoverview.card3_heading"
                as="h3"
                className="po-card-heading font-heading"
              />
              <EditableSlotGroup
                groupPath="propertyoverview.card3_cta"
                definition={card3CtaDef}
                className="po-card-cta-wrap"
              >
                <a
                  href={card3Cta.url || '/amenities'}
                  target={card3Cta._target || '_self'}
                  className="comfy-cta"
                >
                  {card3Cta.label || 'Browse Our Amenities'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .po-section {
          position: relative;
          isolation: isolate;
          background: var(--vibe-color-background);
          padding: 0.75rem 1.25rem 4.5rem;
        }

        @media (min-width: 640px) {
          .po-section {
            padding-top: 1.5rem;
          }
        }

        .po-inner {
          max-width: 1200px;
          margin: 0 auto;
        }

        .po-header {
          text-align: center;
          max-width: 60rem;
          margin: 0 auto 3rem;
        }

        .po-title {
          margin: 0 0 1.5rem;
          font-size: clamp(2rem, 5vw, 3.25rem);
          line-height: 1.08;
          font-weight: 700;
          letter-spacing: 0.01em;
          text-transform: uppercase;
          color: var(--vibe-color-text);
        }

        .po-blurb {
          margin: 0 auto;
          max-width: 52rem;
          font-size: 1.05rem;
          line-height: 1.75;
          color: var(--vibe-color-text-muted);
        }

        .po-blurb p {
          margin: 0 0 1rem;
        }

        .po-blurb p:last-child {
          margin-bottom: 0;
        }

        .po-cards {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }

        .po-card {
          position: relative;
          overflow: hidden;
          aspect-ratio: 4 / 3;
          background: var(--vibe-color-surface);
        }

        .po-card-media {
          position: absolute;
          inset: 0;
        }

        .po-card-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .po-card:hover .po-card-img {
          transform: scale(1.06);
        }

        .po-card-scrim {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            180deg,
            rgba(0, 0, 0, 0.1) 0%,
            rgba(0, 0, 0, 0.28) 45%,
            rgba(0, 0, 0, 0.5) 100%
          );
          pointer-events: none;
        }

        .po-card-content {
          position: absolute;
          inset: 0;
          /* Grid keeps the CTA row pinned to the bottom so it doesn't drift
             when the heading text grows or shrinks. The 1fr row above centers
             the heading in whatever space remains. */
          display: grid;
          grid-template-rows: 1fr auto;
          justify-items: center;
          gap: 1rem;
          padding: 1.75rem;
          text-align: center;
        }

        .po-card-heading {
          align-self: center;
        }

        .po-card-cta-wrap {
          align-self: end;
        }

        .po-card-content .comfy-cta {
          white-space: nowrap;
          max-width: none;
        }

        .po-card-heading {
          margin: 0;
          font-size: clamp(1.5rem, 3.2vw, 2rem);
          line-height: 1.1;
          font-weight: 700;
          text-transform: uppercase;
          text-align: center;
          color: #ffffff;
          text-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
        }

        @media (min-width: 640px) {
          .po-cards {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }

        /* Tablet: cards go 3-across but are narrow, so scale down the heading,
           padding, and CTA to keep the content from wrapping/overflowing. */
        @media (min-width: 640px) and (max-width: 1023px) {
          .po-card-content {
            gap: 0.75rem;
            padding: 1rem;
          }

          .po-card-heading {
            font-size: clamp(1.05rem, 2.6vw, 1.4rem);
          }

          .po-card-content .comfy-cta {
            padding: 0.65rem 1.1rem;
            font-size: 0.7rem;
            letter-spacing: 0.06em;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'A Premier Downtown Destination',
    },
    'propertyoverview.blurb': {
      label: 'Blurb',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue:
        "<p>Look no further for a place that feels like home. Our spacious apartments are designed for quality, comfort, and convenience, with a central location that puts everything you need right outside your doorstep.</p><p>Choose from a variety of brand-new floor plans, featuring modern kitchens, retreat-inspired amenities, &amp; much more to fit your lifestyle.</p>",
    },
    'propertyoverview.card1_heading': {
      label: 'Card 1 Heading',
      type: 'richtext',
      defaultValue: 'Spacious Layouts',
    },
    'propertyoverview.card2_heading': {
      label: 'Card 2 Heading',
      type: 'richtext',
      defaultValue: 'Picture Yourself Here',
    },
    'propertyoverview.card3_heading': {
      label: 'Card 3 Heading',
      type: 'richtext',
      defaultValue: 'Convenience All Around',
    },
  },
  slotGroups: {
    'propertyoverview.card1_image': card1ImageDef,
    'propertyoverview.card2_image': card2ImageDef,
    'propertyoverview.card3_image': card3ImageDef,
    'propertyoverview.card1_cta': card1CtaDef,
    'propertyoverview.card2_cta': card2CtaDef,
    'propertyoverview.card3_cta': card3CtaDef,
  },
};

(PropertyOverview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyOverview;
